<template>
  <div>
    <provisioning-status @refresh="load" />
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>マルチクラウド一覧</span>
        <manual-button path="/multi-cloud-list/" />
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <div>
          <h3 class="ml-3">クラウド接続</h3>
          <div class="d-flex flex-row ga-1 justify-end mb-2">
            <v-btn
              v-if="cloudVnResettingResult.cloudVnConnectTotal > 0"
              v-permission.config
              color="error"
              @click="showCloudVnResetting"
              >VNコネクト設定未完了一覧</v-btn
            >
            <v-btn :href="downloadUrl" download color="primary"
              >一覧ダウンロード</v-btn
            >
            <v-btn color="primary" @click="load">一覧更新</v-btn>
          </div>
          <app-table
            primary-key="lineSeq"
            v-model:table-sort-by="lineList.sortBy"
            :fields="lineList.fields"
            :items="lineList.items"
            :hover="true"
            @click:row="showCloudServices"
            :tr-class="rowHighlight"
          >
            <template #cell(accessType)="{ item }">
              {{ $filter.enumTo(item.accessType, "cloudVn_accessType") }}({{
                $filter.bandwidthTo(item.bandwidth, "Mbps")
              }})
            </template>
          </app-table>
        </div>
        <div v-if="hasSelectedLine" class="mt-4">
          <h3>クラウドサービス</h3>
          <div class="d-flex flex-row ga-1 justify-end mb-2">
            <v-btn v-permission.config color="primary" @click="addMS"
              >Microsoft追加</v-btn
            >
            <v-btn v-permission.config color="primary" @click="deleteMS"
              >Microsoft削除</v-btn
            >
            <v-btn
              v-permission.config
              color="primary"
              @click="showBandwidthLimit"
              >帯域上限値設定</v-btn
            >
          </div>
          <v-alert v-if="alertMsg" class="my-2" :text="alertMsg"></v-alert>
          <div class="mt-2">
            <app-table
              primary-key="cloudLineSeq"
              :fields="lineCloudList.fields"
              :items="lineCloudList.items"
              :selectable="selectable"
              :hover="true"
              v-model="lineCloudList.selected"
              @click:row="showDetails"
              :tr-class="rowClass"
              :td-class="cellClass"
              sticky-header="50vh"
            >
              <template #cell(_selected)="data">
                <v-checkbox-btn
                  v-if="data.item.cloudServiceMenu.startsWith('MS')"
                  :value="data.item"
                  v-model="lineCloudList.selected"
                  @click.stop
                />
              </template>
              <template #cell(cloudServiceMenu)="{ item }">
                {{ $filter.cloudServiceMenuTo(item.cloudServiceMenu) }}
              </template>
              <template #cell(bandwidth)="{ item }">
                {{ $filter.bandwidthTo(item.bandwidth, "Mbps") }}
              </template>
              <template #cell(description)="{ item }">
                {{ $filter.emptyTo(item.description, "-") }}
              </template>
            </app-table>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { AppTableHeader, SortItem } from "@/components/AppTable.vue";
import MultiCloudMsVnConnectionList from "@/modals/multicloud/ms/MultiCloudMsVnConnectionList.vue";
import MicrosoftRegister from "@/modals/multicloud/MicrosoftRegister.vue";
import type { CloudLine, Line, LineList } from "@/apis/CloudLineApi";
import MultiCloudKcps3VnConnectionList from "@/modals/multicloud/kcps3/MultiCloudKcps3VnConnectionList.vue";
import MultiCloudGpuVnConnectionList from "@/modals/multicloud/gpu/MultiCloudGpuVnConnectionList.vue";
import MultiCloudAwsVnConnectionList from "@/modals/multicloud/aws/MultiCloudAwsVnConnectionList.vue";
import MultiCloudOracleVnConnectionList from "@/modals/multicloud/oracle/MultiCloudOracleVnConnectionList.vue";
import MultiCloudGcpVnConnectionList from "@/modals/multicloud/gcp/MultiCloudGcpVnConnectionList.vue";
import MultiCloudIbmVnConnectionList from "@/modals/multicloud/ibm/MultiCloudIbmVnConnectionList.vue";
import MultiCloudSfdcVnConnectionList from "@/modals/multicloud/salesforce/MultiCloudSfdcVnConnectionList.vue";
import type { CloudVnResetting } from "@/apis/CloudVnResettingApi";
import CloudVnResettingList from "@/modals/multicloud/CloudVnResettingList.vue";
import MultiCloudBandwidthLimitModify from "@/modals/multicloud/MultiCloudBandwidthLimitModify.vue";

export default defineComponent({
  name: "MultiCloudList",
  data() {
    return {
      searchResult: {} as LineList,
      selectedLineInfo: null as null | Line,
      lineList: {
        sortBy: { key: "enumber", order: "desc" } as SortItem,
        fields: [
          { key: "enumber", title: "回線番号", width: "200px" },
          { key: "accessType", title: "アクセス回線メニュー", width: "300px" },
        ] as AppTableHeader<Line>[],
        items: [] as Line[],
      },
      lineCloudList: {
        fields: [
          { key: "cloudServiceMenu", title: "メニュー", width: "200px" },
          { key: "bandwidth", title: "帯域上限値", width: "200px" },
          { key: "description", title: "備考", width: "300px" },
        ] as AppTableHeader<CloudLine>[],
        items: [] as CloudLine[],
        selected: [] as CloudLine[],
      },
      cloudVnResettingResult: {} as CloudVnResetting,
      // 初期通信完了フラグ
      isLoaded: false,
      // エラーメッセージ
      alertMsg: null as string | null,
      // download用URL
      downloadUrl: this.$api.cloudLine.getCsvVnConnectURL(),
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    async load() {
      this.alertMsg = null;
      this.searchResult = await this.$api.cloudLine.getCloudLines();
      // GET時、KCPS3はクラウドサービスに一番上に配置する並び替え
      this.searchResult.lineList.forEach((line) => {
        if (Array.isArray(line.cloudLineList)) {
          const kcps3Items = line.cloudLineList.filter(
            (item) => item.cloudServiceMenu === "KCPS3"
          );
          const GPUItems = line.cloudLineList.filter(
            (item) => item.cloudServiceMenu === "KDDI_GPU_CLOUD"
          );
          const otherItems = line.cloudLineList.filter(
            (item) =>
              item.cloudServiceMenu !== "KCPS3" &&
              item.cloudServiceMenu !== "KDDI_GPU_CLOUD"
          );
          line.cloudLineList = kcps3Items.concat(GPUItems).concat(otherItems);
        }
      });

      this.lineList.items = this.searchResult.lineList;
      // 選択済の回線情報があれば、データが変わった可能性があ流ので、再度指定する
      if (this.selectedLineInfo) {
        const currentLine = this.lineList.items.find(
          (v) => v.lineSeq === this.selectedLineInfo!.lineSeq
        );
        if (currentLine) {
          this.showCloudServices(currentLine);
        } else {
          this.selectedLineInfo = null;
        }
      }
      this.cloudVnResettingResult =
        await this.$api.cloudVnResetting.getCloudVnResetting();
    },

    showCloudServices(entity: Line) {
      this.alertMsg = null;
      this.selectedLineInfo = entity;
      this.lineCloudList.items = this.selectedLineInfo.cloudLineList;
    },

    rowHighlight(item: Line): string[] | undefined {
      if (item.lineSeq === this.selectedLineInfo?.lineSeq) {
        return ["table-success"];
      }
      return ["clickable"];
    },

    /** Microsoft追加 */
    async addMS() {
      if (this.lineCloudList.items.length >= 50) {
        this.alertMsg = this.$filter.message("max_vn_connects", {
          maxCount: 50,
        });
        return;
      }
      this.alertMsg = null;
      await this.$modal.show(MicrosoftRegister, {
        enumber: this.selectedLineInfo!.enumber,
        cloudLineList: [...this.selectedLineInfo!.cloudLineList],
        cloudLineKeyId: this.searchResult.cloudLineKeyId,
      });
      await this.load();
    },

    /** Microsoft削除 */
    async deleteMS() {
      if (
        this.lineCloudList.selected.length === 0 ||
        this.lineCloudList.selected.length > 1
      ) {
        this.alertMsg = "Microsoftを１つ選択してください。";
        return;
      }
      if (
        this.lineCloudList.selected.length === 1 &&
        this.lineCloudList.items.filter((v) =>
          v.cloudServiceMenu.startsWith("MS")
        ).length === 1
      ) {
        this.alertMsg = "Microsoftが１つしかない場合、削除できません。";
        return;
      }
      this.alertMsg = null;
      const cloudServiceMenu = this.$filter.cloudServiceMenuTo(
        this.lineCloudList.selected[0].cloudServiceMenu
      );
      await this.$confirm(`${cloudServiceMenu}を削除します。よろしいですか？`);
      await this.$api.cloudLine.deleteMsCloudLine(
        this.lineCloudList.selected[0].cloudLineSeq,
        { cloudLineKeyId: this.searchResult.cloudLineKeyId }
      );
      await this.load();
    },

    /** 帯域上限値設定 */
    async showBandwidthLimit() {
      this.alertMsg = null;
      await this.$modal.show(MultiCloudBandwidthLimitModify, {
        enumber: this.selectedLineInfo!.enumber,
        bandwidth: this.selectedLineInfo!.bandwidth,
        cloudLineList: this.selectedLineInfo!.cloudLineList,
        cloudLineKeyId: this.searchResult.cloudLineKeyId,
      });
      await this.load();
    },

    /** trにクラスをつける */
    rowClass(item: CloudLine): string[] | undefined {
      if (item.cloudServiceMenu === "KCPS3") {
        return ["clickable"];
      }
      return item.bandwidth.value === 0 ? ["un-clickable"] : ["clickable"];
    },

    /** tdにクラスをつける */
    cellClass(field: AppTableHeader<CloudLine>): string[] | undefined {
      if (field.key === "description") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },

    showDetails(item: CloudLine) {
      if (item.bandwidth.value === 0 && item.cloudServiceMenu !== "KCPS3") {
        return;
      }
      this.alertMsg = null;
      // 該当クラウドメニューの設定未完了VNコネクト有無を判断する
      const hasCloudVnResetting =
        this.cloudVnResettingResult.cloudVnConnectsList.some((v) => {
          return (
            v.cloudLineInfo.cloudServiceMenu === item.cloudServiceMenu &&
            v.cloudLineInfo.enumber === this.selectedLineInfo!.enumber
          );
        });
      const bindData = {
        cloudLineSeq: item.cloudLineSeq,
        hasCloudVnResetting:
          item.cloudServiceMenu !== "KCPS3" &&
          item.cloudServiceMenu !== "KDDI_GPU_CLOUD"
            ? hasCloudVnResetting
            : undefined,
        cloudServiceMenu: item.cloudServiceMenu,
        accessLineMenuBandwidth: this.selectedLineInfo!.bandwidth,
      };
      if (item.cloudServiceMenu.startsWith("MS")) {
        this.$modal.show(MultiCloudMsVnConnectionList, bindData);
        return;
      }
      switch (item.cloudServiceMenu) {
        case "KCPS3":
          this.$modal.show(MultiCloudKcps3VnConnectionList, bindData);
          break;
        case "KDDI_GPU_CLOUD":
          this.$modal.show(MultiCloudGpuVnConnectionList, bindData);
          break;
        case "AWS":
          this.$modal.show(MultiCloudAwsVnConnectionList, bindData);
          break;
        case "Oracle":
          this.$modal.show(MultiCloudOracleVnConnectionList, bindData);
          break;
        case "GCP":
          this.$modal.show(MultiCloudGcpVnConnectionList, bindData);
          break;
        case "IBM":
          this.$modal.show(MultiCloudIbmVnConnectionList, bindData);
          break;
        case "SFDC":
          this.$modal.show(MultiCloudSfdcVnConnectionList, bindData);
          break;
      }
    },

    /** VNコネクト設定未完了一覧 */
    async showCloudVnResetting() {
      this.alertMsg = null;
      await this.$modal.show(CloudVnResettingList, {
        cloudVnResetting: this.cloudVnResettingResult,
      });
      await this.load();
    },
  },
  computed: {
    /** true: 一覧選択可能, false: 不可 */
    selectable(): boolean {
      return this.$service.permission.hasAuthority("CONFIG");
    },
    hasSelectedLine(): boolean {
      return !!this.selectedLineInfo;
    },
  },
});

// 帯域上限値の選択肢(以降の画面に使うので)
export const maxBandwidthOptions: {
  value: { value: number; unit: "KBPS" | "MBPS" | "GBPS" | null };
  text: string;
}[] = [
  { value: { value: 0, unit: null }, text: "0Mbps" },
  { value: { value: 10, unit: "MBPS" }, text: "10Mbps" },
  { value: { value: 20, unit: "MBPS" }, text: "20Mbps" },
  { value: { value: 30, unit: "MBPS" }, text: "30Mbps" },
  { value: { value: 40, unit: "MBPS" }, text: "40Mbps" },
  { value: { value: 50, unit: "MBPS" }, text: "50Mbps" },
  { value: { value: 60, unit: "MBPS" }, text: "60Mbps" },
  { value: { value: 70, unit: "MBPS" }, text: "70Mbps" },
  { value: { value: 80, unit: "MBPS" }, text: "80Mbps" },
  { value: { value: 90, unit: "MBPS" }, text: "90Mbps" },
  { value: { value: 100, unit: "MBPS" }, text: "100Mbps" },
  { value: { value: 200, unit: "MBPS" }, text: "200Mbps" },
  { value: { value: 300, unit: "MBPS" }, text: "300Mbps" },
  { value: { value: 400, unit: "MBPS" }, text: "400Mbps" },
  { value: { value: 500, unit: "MBPS" }, text: "500Mbps" },
  { value: { value: 600, unit: "MBPS" }, text: "600Mbps" },
  { value: { value: 700, unit: "MBPS" }, text: "700Mbps" },
  { value: { value: 800, unit: "MBPS" }, text: "800Mbps" },
  { value: { value: 900, unit: "MBPS" }, text: "900Mbps" },
  { value: { value: 1, unit: "GBPS" }, text: "1Gbps" },
  { value: { value: 2, unit: "GBPS" }, text: "2Gbps" },
  { value: { value: 3, unit: "GBPS" }, text: "3Gbps" },
  { value: { value: 4, unit: "GBPS" }, text: "4Gbps" },
  { value: { value: 5, unit: "GBPS" }, text: "5Gbps" },
  { value: { value: 6, unit: "GBPS" }, text: "6Gbps" },
  { value: { value: 7, unit: "GBPS" }, text: "7Gbps" },
  { value: { value: 8, unit: "GBPS" }, text: "8Gbps" },
  { value: { value: 9, unit: "GBPS" }, text: "9Gbps" },
  { value: { value: 10, unit: "GBPS" }, text: "10Gbps" },
  { value: { value: 11, unit: "GBPS" }, text: "11Gbps" },
  { value: { value: 12, unit: "GBPS" }, text: "12Gbps" },
  { value: { value: 13, unit: "GBPS" }, text: "13Gbps" },
  { value: { value: 14, unit: "GBPS" }, text: "14Gbps" },
  { value: { value: 15, unit: "GBPS" }, text: "15Gbps" },
  { value: { value: 16, unit: "GBPS" }, text: "16Gbps" },
  { value: { value: 17, unit: "GBPS" }, text: "17Gbps" },
  { value: { value: 18, unit: "GBPS" }, text: "18Gbps" },
  { value: { value: 19, unit: "GBPS" }, text: "19Gbps" },
  { value: { value: 20, unit: "GBPS" }, text: "20Gbps" },
  { value: { value: 21, unit: "GBPS" }, text: "21Gbps" },
  { value: { value: 22, unit: "GBPS" }, text: "22Gbps" },
  { value: { value: 23, unit: "GBPS" }, text: "23Gbps" },
  { value: { value: 24, unit: "GBPS" }, text: "24Gbps" },
  { value: { value: 25, unit: "GBPS" }, text: "25Gbps" },
  { value: { value: 26, unit: "GBPS" }, text: "26Gbps" },
  { value: { value: 27, unit: "GBPS" }, text: "27Gbps" },
  { value: { value: 28, unit: "GBPS" }, text: "28Gbps" },
  { value: { value: 29, unit: "GBPS" }, text: "29Gbps" },
  { value: { value: 30, unit: "GBPS" }, text: "30Gbps" },
  { value: { value: 31, unit: "GBPS" }, text: "31Gbps" },
  { value: { value: 32, unit: "GBPS" }, text: "32Gbps" },
  { value: { value: 33, unit: "GBPS" }, text: "33Gbps" },
  { value: { value: 34, unit: "GBPS" }, text: "34Gbps" },
  { value: { value: 35, unit: "GBPS" }, text: "35Gbps" },
  { value: { value: 36, unit: "GBPS" }, text: "36Gbps" },
  { value: { value: 37, unit: "GBPS" }, text: "37Gbps" },
  { value: { value: 38, unit: "GBPS" }, text: "38Gbps" },
  { value: { value: 39, unit: "GBPS" }, text: "39Gbps" },
  { value: { value: 40, unit: "GBPS" }, text: "40Gbps" },
  { value: { value: 41, unit: "GBPS" }, text: "41Gbps" },
  { value: { value: 42, unit: "GBPS" }, text: "42Gbps" },
  { value: { value: 43, unit: "GBPS" }, text: "43Gbps" },
  { value: { value: 44, unit: "GBPS" }, text: "44Gbps" },
  { value: { value: 45, unit: "GBPS" }, text: "45Gbps" },
  { value: { value: 46, unit: "GBPS" }, text: "46Gbps" },
  { value: { value: 47, unit: "GBPS" }, text: "47Gbps" },
  { value: { value: 48, unit: "GBPS" }, text: "48Gbps" },
  { value: { value: 49, unit: "GBPS" }, text: "49Gbps" },
  { value: { value: 50, unit: "GBPS" }, text: "50Gbps" },
  { value: { value: 51, unit: "GBPS" }, text: "51Gbps" },
  { value: { value: 52, unit: "GBPS" }, text: "52Gbps" },
  { value: { value: 53, unit: "GBPS" }, text: "53Gbps" },
  { value: { value: 54, unit: "GBPS" }, text: "54Gbps" },
  { value: { value: 55, unit: "GBPS" }, text: "55Gbps" },
  { value: { value: 56, unit: "GBPS" }, text: "56Gbps" },
  { value: { value: 57, unit: "GBPS" }, text: "57Gbps" },
  { value: { value: 58, unit: "GBPS" }, text: "58Gbps" },
  { value: { value: 59, unit: "GBPS" }, text: "59Gbps" },
  { value: { value: 60, unit: "GBPS" }, text: "60Gbps" },
  { value: { value: 61, unit: "GBPS" }, text: "61Gbps" },
  { value: { value: 62, unit: "GBPS" }, text: "62Gbps" },
  { value: { value: 63, unit: "GBPS" }, text: "63Gbps" },
  { value: { value: 64, unit: "GBPS" }, text: "64Gbps" },
  { value: { value: 65, unit: "GBPS" }, text: "65Gbps" },
  { value: { value: 66, unit: "GBPS" }, text: "66Gbps" },
  { value: { value: 67, unit: "GBPS" }, text: "67Gbps" },
  { value: { value: 68, unit: "GBPS" }, text: "68Gbps" },
  { value: { value: 69, unit: "GBPS" }, text: "69Gbps" },
  { value: { value: 70, unit: "GBPS" }, text: "70Gbps" },
  { value: { value: 71, unit: "GBPS" }, text: "71Gbps" },
  { value: { value: 72, unit: "GBPS" }, text: "72Gbps" },
  { value: { value: 73, unit: "GBPS" }, text: "73Gbps" },
  { value: { value: 74, unit: "GBPS" }, text: "74Gbps" },
  { value: { value: 75, unit: "GBPS" }, text: "75Gbps" },
  { value: { value: 76, unit: "GBPS" }, text: "76Gbps" },
  { value: { value: 77, unit: "GBPS" }, text: "77Gbps" },
  { value: { value: 78, unit: "GBPS" }, text: "78Gbps" },
  { value: { value: 79, unit: "GBPS" }, text: "79Gbps" },
  { value: { value: 80, unit: "GBPS" }, text: "80Gbps" },
  { value: { value: 81, unit: "GBPS" }, text: "81Gbps" },
  { value: { value: 82, unit: "GBPS" }, text: "82Gbps" },
  { value: { value: 83, unit: "GBPS" }, text: "83Gbps" },
  { value: { value: 84, unit: "GBPS" }, text: "84Gbps" },
  { value: { value: 85, unit: "GBPS" }, text: "85Gbps" },
  { value: { value: 86, unit: "GBPS" }, text: "86Gbps" },
  { value: { value: 87, unit: "GBPS" }, text: "87Gbps" },
  { value: { value: 88, unit: "GBPS" }, text: "88Gbps" },
  { value: { value: 89, unit: "GBPS" }, text: "89Gbps" },
  { value: { value: 90, unit: "GBPS" }, text: "90Gbps" },
  { value: { value: 91, unit: "GBPS" }, text: "91Gbps" },
  { value: { value: 92, unit: "GBPS" }, text: "92Gbps" },
  { value: { value: 93, unit: "GBPS" }, text: "93Gbps" },
  { value: { value: 94, unit: "GBPS" }, text: "94Gbps" },
  { value: { value: 95, unit: "GBPS" }, text: "95Gbps" },
  { value: { value: 96, unit: "GBPS" }, text: "96Gbps" },
  { value: { value: 97, unit: "GBPS" }, text: "97Gbps" },
  { value: { value: 98, unit: "GBPS" }, text: "98Gbps" },
  { value: { value: 99, unit: "GBPS" }, text: "99Gbps" },
  { value: { value: 100, unit: "GBPS" }, text: "100Gbps" },
];
</script>
